import React, { useState, useEffect, useRef, useContext, version } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { debounce } from 'debounce';
import Image from '../Image';
import Icon from '../Icon';
import Tag from '../Tag';
import Player from '../Player/Player';
import Button from '../Button/Button';
import SingleChooser from '../SingleChooser/SingleChooser';
import styles from './PlayableAudioCard.module.scss';
//import { useSampleDownloadSelector } from "../../utils/useSampleDownloadSelector";
import Modal from '../Modal';
import Chip from '../Chip';
import DownloadProductChooserModal from '../DownloadProductChooserModal';
import PianoDownloadTableModal from '../PianoDownloadTableModal';
import { httpGet } from '../../utils/Http';
import { keysToCamelFromSnake } from '../../utils/caseconverters';
import { scroller } from 'react-scroll';
import Link from 'next/link';
import { AccordionContext } from '../../contexts/AccordionContext';
import {
    getSelectedProductIdFromCookie,
    useProductSelection,
} from '../../utils/singleProductChooser';

const NEXT_PUBLIC_API_DOMAIN = process.env.NEXT_PUBLIC_API_DOMAIN;

const PlayableAudioCard = ({
    image = {},
    title = '',
    text = '',
    link = {},
    tags = [],
    row = false,
    play = {},
    id,
    download = '',
    modalText,
    accordionItems,
    selectedProductId = null,
    compatibleProducts,
    pianoDownloads,
    playerData,
    hasTags,
    defaultAccordionSortOrder = null,
    onPlay,
    onPause,
    isHighlight = false,
    triggerProductChooserOnDownload = false,
    forceContainImage = false,
    type = null,
    productSelectionOptions,
    latestVersions,
    selectedProductLatestSoundVersionNumber,
    disableHoverEffect = false,
}) => {
    const { isExpandedAll, setIsExpandedAll } = useContext(AccordionContext);
    const [isExpanded, setIsExpanded] = useState(false);
    const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
    const [_compatibleProducts, setCompatibleProducts] = useState(
        Array.isArray(compatibleProducts) ? compatibleProducts : []
    );
    const { selectedProduct } = useProductSelection({
        ...productSelectionOptions,
        selectedProductId,
    });

    const playerRef = useRef(null);

    useEffect(() => {
        if (typeof compatibleProducts === 'string') {
            httpGet(NEXT_PUBLIC_API_DOMAIN + compatibleProducts).then(
                (data) => {
                    setCompatibleProducts(keysToCamelFromSnake(data));
                }
            );
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                playerRef.current &&
                !playerRef.current.contains(event.target)
            ) {
                onPause();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onPause]);

    const hasAccordion = !!accordionItems;

    const stopCurrentlyPlaying = () => {
        if (currentlyPlaying) {
            currentlyPlaying.pause();
        }
    };

    const handleExpandOnClick = () => {
        setIsExpanded(!isExpanded);
        if (typeof window.dataLayer === 'undefined') return;
    };

    useEffect(() => {
        if (isExpandedAll) {
            setIsExpanded(true);
        } else {
            setIsExpanded(false);
        }
    }, [isExpandedAll]);

    const [subcategories, setSubcategories] = useState([]);
    const [fetchedAccordionItems, setFetchedAccordionItems] = useState([]);

    useEffect(() => {
        if (hasAccordion && isExpanded) {
            httpGet(NEXT_PUBLIC_API_DOMAIN + accordionItems).then((data) => {
                setFetchedAccordionItems(
                    keysToCamelFromSnake(data['accordion_items'])
                );
                setSubcategories(keysToCamelFromSnake(data['subcategories']));
            });
        }
    }, [isExpanded, accordionItems]);
    const forceModal =
        isHighlight && !selectedProductId && type !== 'SignatureSoundBankPage';

    const dataLayerBaseData = {
        event: 'sounds_download',
        collection_name: type,
        product_name: null,
        sample_name: null,
        sound_name: title,
        version: null,
    };

    const displayLatestVersion =
        latestVersions &&
        Object.keys(latestVersions).length === 1 &&
        latestVersions[5]
            ? latestVersions[5]
            : latestVersions?.[selectedProductLatestSoundVersionNumber];

    const handleDataLayerPushPianoLibraryPage = (download) => {
        const versionName = download.versionName
            ? `${download.size.toUpperCase()} version - ${title} - v${
                  download.versionName
              }`
            : `${download.size.toUpperCase()} version - ${title}`;

        const data = {
            ...dataLayerBaseData,
            version: versionName,
            product_name: selectedProductId
                ? selectedProduct.label
                : download.selectedProduct,
        };

        if (typeof window.dataLayer !== 'undefined') {
            window.dataLayer.push(data);
            console.log('Data pushed to dataLayer:', data);
            console.log('Current dataLayer:', window.dataLayer);
        } else {
            console.error('dataLayer is not defined');
        }
    };

    const handleDataLayerPushSignatureSoundBankPage = (product) => {
        const data = {
            ...dataLayerBaseData,
            product_name: selectedProductId
                ? selectedProduct.label
                : product.title,
        };

        if (typeof window.dataLayer !== 'undefined') {
            window.dataLayer.push(data);
            console.log('Data pushed to dataLayer:', data);
            console.log('Current dataLayer:', window.dataLayer);
        } else {
            console.error('dataLayer is not defined');
        }
    };

    const handleDownloadClick = (downloadType) => {
        const data = {
            ...dataLayerBaseData,
            sample_name: downloadType.title
                ? downloadType.title
                : 'Download all',
            version: downloadType.latestVersion
                ? `${downloadType.title} - ${downloadType.latestVersion}`
                : null,
            product_name: selectedProductId ? selectedProduct.label : null,
        };

        if (typeof window.dataLayer !== 'undefined') {
            window.dataLayer.push(data);
            console.log('Data pushed to dataLayer:', data);
            console.log('Current dataLayer:', window.dataLayer);
        } else {
            console.error('dataLayer is not defined');
        }

        window.location = download;
    };

    const cardContent = (
        <>
            <div className={styles['PlayableAudioCard__Tag']}>
                {tags &&
                    hasTags &&
                    tags.map((tag, i) => (
                        <Tag color={tag.color} tag={tag.tag} key={i} />
                    ))}
            </div>
            <div className={styles['PlayableAudioCard__ImageContainer']}>
                {image.url && (
                    <Image
                        {...image}
                        objectFit={
                            row
                                ? 'contain'
                                : forceContainImage
                                ? 'contain'
                                : 'cover'
                        }
                    />
                )}
            </div>
            <div className={styles['PlayableAudioCard__ContentContainer']}>
                <div className={styles['PlayableAudioCard__InfoWrapper']}>
                    <div className={styles['PlayableAudioCard__Title']}>
                        <h3>{title}</h3>
                        {displayLatestVersion && (
                            <span
                                className={
                                    styles['PlayableAudioCard__LatestVersion']
                                }>
                                v{displayLatestVersion}
                            </span>
                        )}
                    </div>
                    <div className={styles['PlayableAudioCard__Description']}>
                        <p>{text}</p>
                    </div>
                </div>
            </div>
        </>
    );

    return (
        <React.Fragment>
            <article
                id={`card-${id}`}
                className={classNames(styles['PlayableAudioCard'], {
                    [styles['PlayableAudioCard--Row']]: row && !hasAccordion,
                    [styles['PlayableAudioCard--Accordion']]: hasAccordion,
                    [styles['PlayableAudioCard--NoHover']]: disableHoverEffect,
                })}>
                {hasAccordion ? (
                    <button
                        className={
                            styles['PlayableAudioCard__ClickableContainer']
                        }
                        aria-expanded={isExpanded}
                        aria-controls={id}
                        onClick={() => handleExpandOnClick()}>
                        {cardContent}
                    </button>
                ) : link.href ? (
                    <Link
                        href={link.href}
                        className={
                            styles['PlayableAudioCard__ClickableContainer']
                        }>
                        {cardContent}
                    </Link>
                ) : (
                    <div
                        className={
                            styles['PlayableAudioCard__ClickableContainer']
                        }>
                        {cardContent}
                    </div>
                )}
                <div className={styles['PlayableAudioCard__LinkList']}>
                    {playerData && !hasAccordion && (
                        <span
                            className={styles['PlayableAudioCard__PlayButton']}
                            ref={playerRef}>
                            <Player
                                audio={playerData}
                                card={true}
                                onPlay={onPlay}
                                onPause={onPause}
                            />
                        </span>
                    )}

                    {download && !forceModal ? (
                        <span
                            className={
                                styles['PlayableAudioCard__DownloadButton']
                            }>
                            {row ? (
                                <Button
                                    text={'Download all'}
                                    type={'tertiary'}
                                    icon={'download'}
                                    iconDirection={'right'}
                                    onClick={() =>
                                        handleDownloadClick('Download all')
                                    }
                                    modifiers={['fontSmall']}
                                />
                            ) : (
                                <Button
                                    text={'Download all'}
                                    type={'onlyIcon'}
                                    icon={'download'}
                                    onClick={() =>
                                        handleDownloadClick('Download all')
                                    }
                                />
                            )}
                        </span>
                    ) : (
                        <Modal
                            modifiers={['alternative']}
                            buttonText="Download all"
                            buttonType={'onlyIcon'}
                            buttonIcon={'download'}
                            hasAccordion={hasAccordion}>
                            {pianoDownloads && (
                                <PianoDownloadTableModal
                                    downloads={pianoDownloads}
                                    title={title}
                                    modalText={modalText}
                                    triggerProductChooser={
                                        triggerProductChooserOnDownload
                                    }
                                    pageType={type}
                                    onDownload={
                                        handleDataLayerPushPianoLibraryPage
                                    }
                                />
                            )}

                            {!pianoDownloads &&
                                _compatibleProducts &&
                                _compatibleProducts.activeProducts && (
                                    <DownloadProductChooserModal
                                        downloadUrl={
                                            forceModal ? null : download
                                        }
                                        products={_compatibleProducts.activeProducts.map(
                                            (prod) => ({
                                                title: prod.label,
                                                downloadUrl: prod.downloadUrl,
                                            })
                                        )}
                                        legacyProducts={_compatibleProducts.legacyProducts.map(
                                            (prod) => ({
                                                title: prod.label,
                                                downloadUrl: prod.downloadUrl,
                                            })
                                        )}
                                        title={title}
                                        onDownload={
                                            handleDataLayerPushSignatureSoundBankPage
                                        }
                                    />
                                )}

                            {!pianoDownloads &&
                                !_compatibleProducts?.activeProducts && (
                                    <p>
                                        Download is missing. Please contact our
                                        customer service and we will try to
                                        resolve the issue as soon as possible
                                    </p>
                                )}
                        </Modal>
                    )}
                </div>
            </article>
            <div>
                {hasAccordion && (
                    <PlayableAudioCardAccordion
                        onLinkDownload={handleDownloadClick}
                        key={accordionItems}
                        id={id}
                        title={title}
                        playerData={playerData}
                        compatibleProducts={compatibleProducts}
                        selectedProductId={selectedProductId}
                        row={row}
                        card={false}
                        defaultAccordionSortOrder={defaultAccordionSortOrder}
                        isExpanded={isExpanded}
                        isHighlight={isHighlight}
                        handleExpandOnClick={handleExpandOnClick}
                        subcategories={subcategories}
                        fetchedAccordionItems={fetchedAccordionItems}
                        onPlay={(player) => {
                            if (currentlyPlaying !== player) {
                                stopCurrentlyPlaying();
                                setCurrentlyPlaying(player);
                            }
                        }}
                        onPause={() => {
                            stopCurrentlyPlaying();
                            setCurrentlyPlaying(null);
                        }}
                    />
                )}
            </div>
        </React.Fragment>
    );
};

PlayableAudioCard.propTypes = {
    image: PropTypes.object,
    title: PropTypes.string,
    tags: PropTypes.array,
    text: PropTypes.string,
    link: PropTypes.shape({
        text: PropTypes.string,
        href: PropTypes.string.isRequired,
    }),
    play: PropTypes.object,
    pause: PropTypes.object,
    share: PropTypes.object,
    download: PropTypes.string,
    row: PropTypes.bool,
    accordion: PropTypes.bool,
    selectedProductId: PropTypes.number,
    onPlay: PropTypes.func.isRequired,
    onPause: PropTypes.func.isRequired,
};

const PlayableAudioCardAccordion = ({
    height,
    title,
    id,
    row,
    defaultAccordionSortOrder,
    onPlay,
    onPause,
    isExpanded,
    handleExpandOnClick,
    isHighlight = false,
    subcategories,
    fetchedAccordionItems,
    fileSize,
    onLinkDownload,
}) => {
    const cookieValue = getSelectedProductIdFromCookie();

    const componentRef = useRef(null);
    const accordionRef = useRef(null);
    const [selectedChipsIds, setSelectedChipsIds] = useState([0]);
    const [sortOrder, setSortOrder] = useState(
        defaultAccordionSortOrder ? defaultAccordionSortOrder : 'alphabetic'
    );
    const [contentHeight, setContentHeight] = useState(height);
    const accordionAnswer = useRef(null);
    const slug = title?.toLowerCase().replace(/\s+/g, '-');
    const cardId = `${slug}-card`;

    const handleResize = debounce(
        () => setContentHeight(accordionAnswer.current.clientHeight),
        300
    );

    const filterItems = (items) =>
        items.filter(
            (x) =>
                selectedChipsIds.includes(x.subcategory?.id) ||
                selectedChipsIds.includes(0)
        );

    const sortItems = (items) => {
        if (sortOrder === 'latest') {
            return [...items].sort((a, b) => {
                const dateA = new Date(a.publishedDate);
                const dateB = new Date(b.publishedDate);
                return dateB - dateA;
            });
        } else if (sortOrder === 'alphabetic') {
            return [...items].sort((a, b) => {
                if (a.title.toLowerCase() < b.title.toLowerCase()) {
                    return -1;
                }
                if (a.title.toLowerCase() > b.title.toLowerCase()) {
                    return 1;
                }
                return 0;
            });
        } else {
            return items;
        }
    };

    // Variables
    const sortOrders = [
        {
            label: 'Alphabetic',
            value: 'alphabetic',
        },
        {
            label: 'Latest',
            value: 'latest',
        },
    ];

    const classes = classNames(styles.Accordion, {
        [styles['PlayableAudioCard--Open']]: isExpanded,
    });

    const handleChipOnClick = (chipId) => {
        if (selectedChipsIds[0] === chipId) {
            setSelectedChipsIds([0]);
        } else {
            setSelectedChipsIds([chipId]);
        }
        onPause();
    };

    // Effects

    useEffect(() => {
        setSortOrder(defaultAccordionSortOrder);
    }, [defaultAccordionSortOrder]);

    useEffect(() => {
        setContentHeight(accordionAnswer.current.clientHeight);
        window.addEventListener('resize', handleResize);

        const handleClickOutside = (event) => {
            if (
                accordionRef.current &&
                !accordionRef.current.contains(event.target)
            ) {
                onPause();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleResize, onPause]);

    const handleAccordionCloseButtonOnClick = () => {
        const shouldScrollUpToElement =
            isExpanded && !isElementInViewport(componentRef);
        handleExpandOnClick(!isExpanded);

        if (shouldScrollUpToElement) {
            const offset = window.innerWidth <= 1024 ? -75 : -124; // The height of the header

            scroller.scrollTo(`card-${id}`, {
                duration: 250,
                delay: 0,
                smooth: 'ease',
                offset: offset - 16,
            });
        }
    };

    const isElementInViewport = (ref) => {
        if (ref && ref.current) {
            const rect = ref.current.getBoundingClientRect();
            return rect.top >= 0 && rect.bottom <= window.innerHeight;
        }
        return false;
    };

    return (
        <div
            ref={componentRef}
            className={classNames({
                [styles['PlayableAudioCard__Accordion']]: !isExpanded,
                [styles['PlayableAudioCard__Accordion--Open']]: isExpanded,
            })}>
            <div className={classes}>
                <div
                    className={styles['PlayableAudioCard__Content']}
                    style={{
                        maxHeight: isExpanded ? contentHeight : 0 + 'px',
                    }}>
                    <div
                        ref={accordionAnswer}
                        aria-labelledby={cardId}
                        aria-hidden={!isExpanded}
                        id={id}>
                        <div
                            className={
                                styles['PlayableAudioCard__AccordionList']
                            }>
                            {row && (
                                <>
                                    <div
                                        className={classNames(
                                            styles[
                                                'PlayableAudioCard__AccordionTitle'
                                            ],
                                            styles[
                                                'PlayableAudioCard__AccordionTitle--Upper'
                                            ]
                                        )}>
                                        <div
                                            className={
                                                styles[
                                                    'PlayableAudioCard__Sort'
                                                ]
                                            }>
                                            <span
                                                className={
                                                    styles[
                                                        'PlayableAudioCard__SortTitle'
                                                    ]
                                                }>
                                                Sort samples:
                                            </span>
                                            <SingleChooser
                                                tiny={true}
                                                onChange={(e) =>
                                                    setSortOrder(e)
                                                }
                                                placeholder={
                                                    sortOrders.find(
                                                        (order) =>
                                                            order.value ===
                                                            sortOrder
                                                    )?.label
                                                }
                                                options={sortOrders}
                                            />
                                        </div>
                                        <button
                                            className={
                                                styles[
                                                    'PlayableAudioCard__AccordionButton'
                                                ]
                                            }
                                            onClick={() =>
                                                handleExpandOnClick()
                                            }
                                            aria-expanded={isExpanded}
                                            aria-controls={id}
                                            id={cardId}>
                                            Hide sounds
                                            <div
                                                className={
                                                    styles[
                                                        'PlayableAudioCard__Chevron'
                                                    ]
                                                }>
                                                <Icon
                                                    type={'chevron'}
                                                    size={'large'}
                                                />
                                            </div>
                                        </button>
                                    </div>

                                    {subcategories &&
                                        subcategories.length > 1 && (
                                            <div
                                                className={
                                                    styles[
                                                        'PlayableAudioCard__Chips'
                                                    ]
                                                }>
                                                <Chip
                                                    title={'All'}
                                                    id={0}
                                                    slug={'all_' + id}
                                                    selected={selectedChipsIds.includes(
                                                        0
                                                    )}
                                                    onClick={(x) =>
                                                        handleChipOnClick(x)
                                                    }
                                                />
                                                {subcategories.map(
                                                    (subcategory, index) => (
                                                        <Chip
                                                            key={
                                                                subcategory.id +
                                                                '-' +
                                                                id
                                                            }
                                                            {...subcategory}
                                                            slug={
                                                                subcategory.id +
                                                                '-' +
                                                                id
                                                            }
                                                            selected={selectedChipsIds.includes(
                                                                subcategory.id
                                                            )}
                                                            onClick={(x) =>
                                                                handleChipOnClick(
                                                                    x
                                                                )
                                                            }
                                                        />
                                                    )
                                                )}
                                            </div>
                                        )}
                                </>
                            )}
                            <ul ref={accordionRef}>
                                {sortItems(
                                    filterItems(fetchedAccordionItems)
                                ).map((item, i) => {
                                    return (
                                        <li
                                            key={item.id}
                                            className={
                                                styles[
                                                    'PlayableAudioCard__AccordionItems'
                                                ]
                                            }>
                                            <div
                                                className={
                                                    styles[
                                                        'PlayableAudioCard__Items'
                                                    ]
                                                }>
                                                <div>
                                                    <h4
                                                        className={
                                                            styles[
                                                                'PlayableAudioCard__ProductTitle'
                                                            ]
                                                        }>
                                                        {item.title}
                                                    </h4>
                                                    <p
                                                        className={
                                                            styles[
                                                                'PlayableAudioCard__ProductType'
                                                            ]
                                                        }>
                                                        {item.type}
                                                    </p>
                                                </div>
                                                {item.latestVersion && (
                                                    <p
                                                        className={
                                                            styles[
                                                                'PlayableAudioCard__Version'
                                                            ]
                                                        }>
                                                        v{item.latestVersion}
                                                    </p>
                                                )}
                                            </div>
                                            <div
                                                className={
                                                    styles[
                                                        'PlayableAudioCard__ActionButtons'
                                                    ]
                                                }>
                                                <Player
                                                    audio={item.previewFile}
                                                    card={true}
                                                    onPlay={onPlay}
                                                    onPause={onPause}
                                                />
                                                <Modal
                                                    modifiers={['alternative']}
                                                    buttonText="Download"
                                                    buttonType={'onlyIcon'}
                                                    buttonIcon={'download'}
                                                    downloadLink={{
                                                        href: item.downloadUrl,
                                                    }}
                                                    onClick={() =>
                                                        onLinkDownload(item)
                                                    }>
                                                    {console.log(item)}
                                                    {item.compatibleProducts && (
                                                        <DownloadProductChooserModal
                                                            compatibleProducts={
                                                                item.compatibleProducts
                                                            }
                                                            title={item.title}
                                                        />
                                                    )}
                                                </Modal>
                                            </div>

                                            {item.fileSize && (
                                                <p
                                                    className={
                                                        styles[
                                                            'PlayableAudioCard__FileSize'
                                                        ]
                                                    }>
                                                    {item.fileSize}
                                                </p>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    </div>
                </div>

                <div className={styles['PlayableAudioCard__AccordionTitle']}>
                    <button
                        className={styles['PlayableAudioCard__AccordionButton']}
                        onClick={() => handleAccordionCloseButtonOnClick()}
                        aria-expanded={isExpanded}
                        aria-controls={id}
                        id={cardId}>
                        {isExpanded ? 'Hide sounds' : 'Show sounds'}

                        <div className={styles['PlayableAudioCard__Chevron']}>
                            <Icon type={'chevron'} size={'large'} />
                        </div>
                    </button>
                </div>
            </div>
        </div>
    );
};

PlayableAudioCardAccordion.propTypes = {
    height: PropTypes.number,
    selectedProductId: PropTypes.number,
    isExpanded: PropTypes.bool,
    id: PropTypes.string,
    triggerProductChooserOnDownload: PropTypes.bool,
    fetchedAccordionItems: PropTypes.array,
    compatibleProducts: PropTypes.shape({
        activeProducts: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                value: PropTypes.number,
            })
        ),
        legacyProducts: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                value: PropTypes.number,
            })
        ),
    }),
    playerData: PropTypes.object,
    onPlay: PropTypes.func.isRequired,
    onPause: PropTypes.func.isRequired,
};

PlayableAudioCardAccordion.defaultProps = {
    height: 0,
    selectedProductId: null,
    id: '',
    isExpanded: false,
    fetchedAccordionItems: [],
    compatibleProducts: null,
    playerData: {},
};

export default PlayableAudioCard;
